/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormEvent } from "react";

import { Button } from "@ezmid/components-library";
import {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  FieldPath,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormInputField } from "../../../components/hookFormComponents/FormInputField";
import { MarkdownField } from "../../../components/hookFormComponents/MarkdownField";
import { SelectInputField } from "../../../components/hookFormComponents/SelectInputField";
import { SwitchField } from "../../../components/hookFormComponents/SwitchField";
import { Tabs } from "../../../components/tabs/Tabs";

interface LessonFormFields extends FieldValues {
  lessonNameDE?: string;
  lessonNameEN?: string;
  textDe?: string;
  textEn?: string;
  order?: number | string;
  isFree?: boolean;
  status?: string;
}

type Props<T extends LessonFormFields> = {
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  control: Control<T>;
  errors: FieldErrors<T>;

  isSubmitButtonDisabled: boolean;
  onSumbit: () => void;
  hasSubmitButton?: boolean;
  lessonId?: string;
  formResetKey?: string;
};

export function LessonDetailForm<T extends LessonFormFields>({
  handleSubmit,
  control,
  errors,
  isSubmitButtonDisabled,
  onSumbit,
  hasSubmitButton = false,
  lessonId,
  formResetKey = "",
}: Props<T>) {
  const { t } = useTranslation();

  const statusOptions = [
    { value: "ls-active", label: t("status.active") },
    { value: "ls-preview", label: t("status.preview") },
    { value: "ls-draft", label: t("status.draft") },
  ];

  const lessonNameDataTabs = [
    {
      label: `${t("app.lesson.name.label")} DE`,
      content: (
        <FormInputField
          key={`lessonNameDE-${lessonId || "new"}`}
          name={"lessonNameDE" as Path<T>}
          htmlFor="lessonNameDE"
          id="lessonNameDE"
          type="text"
          control={control}
          error={(errors as any).lessonNameDE}
          placeholder={t("app.lesson.name.label")}
        />
      ),
    },
    {
      label: `${t("app.lesson.name.label")} EN`,
      content: (
        <FormInputField
          key={`lessonNameEN-${lessonId || "new"}`}
          name={"lessonNameEN" as Path<T>}
          htmlFor="lessonNameEN"
          id="lessonNameEN"
          type="text"
          control={control}
          error={(errors as any).lessonNameEN}
          placeholder={t("app.lesson.name.label")}
        />
      ),
    },
  ];

  const textDataTabs = [
    {
      label: `${t("app.lesson.text.label")} DE`,
      content: (
        <MarkdownField
          key={`textDe-${lessonId || "new"}-${formResetKey}`}
          name={"textDe" as FieldPath<T>}
          htmlFor="textDe"
          id="textDe"
          control={control}
          error={(errors as any).textDe}
          editorKey={`textDe-${lessonId || "new"}-${formResetKey}`}
        />
      ),
    },
    {
      label: `${t("app.lesson.text.label")} EN`,
      content: (
        <MarkdownField
          key={`textEn-${lessonId || "new"}-${formResetKey}`}
          name={"textEn" as FieldPath<T>}
          htmlFor="textEn"
          id="textEn"
          control={control}
          error={(errors as any).textEn}
          editorKey={`textEn-${lessonId || "new"}-${formResetKey}`}
        />
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="flex p-4">
      <div className="w-full">
        <FormInputField
          name={"order" as Path<T>}
          htmlFor="order"
          label="Order"
          id="order"
          type="number"
          control={control}
          placeholder="Order"
          error={(errors as any).order}
        />

        <SelectInputField
          label={t("form.status.label")}
          name={"status" as Path<T>}
          htmlFor="status"
          control={control}
          options={statusOptions}
          defaultValue={statusOptions[2]}
        />

        <Tabs tabsData={lessonNameDataTabs} />
        <Tabs tabsData={textDataTabs} />

        <div className="pt-4 flex justify-start">
          <SwitchField
            control={control}
            name={"isFree" as Path<T>}
            label={t("form.isFree")}
            variant="green"
          />
        </div>

        {!!hasSubmitButton && (
          <div className="flex justify-end mt-4">
            <Button onClick={onSumbit} disabled={isSubmitButtonDisabled}>
              {t("button.save")}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
}
